// bounded-widget-ownership.ts - per-open ownership generation for the singleton
// login/signing modal (bounded-widget-modal.ts).
//
// That modal is a MODULE-LEVEL singleton: one mutable `content` node and one
// mutable `onBackdrop` callback, reused between opens so a second open is instant.
// The hazard is concurrency: two overlapping openBoundedWidget() calls in the same
// tab (rapid clicks, or two components) would otherwise share - and corrupt - one
// surface. The second open overwrites the first's backdrop handler and remounts the
// card while the first promise is never settled, so it hangs forever; and a late
// success from the FIRST open's in-flight popup/OTP would hide or de-wire the
// SECOND card.
//
// The fix is a per-open ownership generation. Each open claims a monotonically
// increasing token stored on the modal. All of an open's callbacks (backdrop
// dismiss, success, cancel, and every async .then/.catch that mutates the shared
// modal) run only while they still hold the CURRENT generation; a superseded open
// no-ops instead of stomping the newer one. Claiming also proactively rejects the
// prior owner's promise so an earlier caller is never stranded.
/**
 * Claim the singleton modal for a fresh open. Any earlier open that never settled
 * is superseded: the generation is bumped first (so the prior open's `owns()`
 * turns false and its late callbacks can no longer touch the modal we now own),
 * then its supersede hook is invoked so its promise rejects.
 *
 * @param onSuperseded rejects THIS open's promise when a later open supersedes it.
 */
export function claimModalOpen(m, onSuperseded) {
    const prevSupersede = m.onSupersede;
    const token = ++m.generation;
    let settled = false;
    const owns = () => token === m.generation;
    const settleOwned = (action) => {
        if (settled || !owns())
            return false;
        settled = true;
        m.onBackdrop = null;
        m.onSupersede = null;
        action();
        return true;
    };
    // Superseded by a newer open: reject this promise WITHOUT touching the modal
    // (the newer open now owns its DOM). Never settle twice.
    m.onSupersede = () => {
        if (settled)
            return;
        settled = true;
        onSuperseded();
    };
    // Now that we hold the newer generation, reject the prior owner (if any).
    prevSupersede === null || prevSupersede === void 0 ? void 0 : prevSupersede();
    return { owns, settleOwned };
}
